<script setup lang="ts">
/**
 * Aide contextuelle du dessin : une ligne, en bas a droite, qui dit ce que
 * l'outil courant attend. Desactivable depuis les reglages (Settings.toolHintsOn)
 * pour rendre l'ecran a l'utilisateur aguerri.
 */
import { onBeforeUnmount, watch, watchEffect } from "vue";
import { useTranslation } from "i18next-vue";
import { Facade } from "@/drawing";
import { getCanvasTextScale } from "@/composable/useCanvasUiScale";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { useToolHint } from "@/composable/useToolHint";
import { isCanvasDrawing, isToolHintVisible } from "@/stores/CanvasViewState";
import { Z_INDEX_HINT } from "./layouts/constants";

const { t } = useTranslation();
const { hintKey } = useToolHint();

// Partagé avec les volets de droite, qui décalent leur pied sous l'aide.
watch(hintKey, (key) => (isToolHintVisible.value = !!key), { immediate: true });

/** Hauteur du cartouche (px de base) : ligne de 16 px, marges, bordure. */
const HINT_HEIGHT = 23;

// La poignée de la règle verticale, en bas du plan, se pose au-dessus de
// l'aide (pixels du canvas = pixels écran).
watchEffect(() => {
  if (!Facade.isInitialized) return;
  Facade.rulers.setVerticalHandleBottomInset(
    isToolHintVisible.value ? HINT_HEIGHT * getCanvasTextScale() : 0,
  );
});

onBeforeUnmount(() => {
  isToolHintVisible.value = false;
  if (Facade.isInitialized) Facade.rulers.setVerticalHandleBottomInset(0);
});
</script>

<template>
  <Transition name="tool-hint">
    <div
      v-if="hintKey"
      class="tool-hint no-capture canvas-text-scaled"
      :class="{ 'drawing-active': isCanvasDrawing }"
      :style="{ zIndex: Z_INDEX_HINT }"
    >
      <PhosphorIcon name="question" :size="12" class="tool-hint-icon" />
      <span class="tool-hint-text">{{ t(hintKey) }}</span>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.tool-hint {
  position: absolute;
  // Collee au coin bas droit de l'ecran : pas de coins arrondis ni de bordure
  // sur les cotes qui touchent le bord.
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  max-width: 40vw;
  padding: 3px 8px;
  background-color: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  color: #6c757d;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

// Une seule ligne : un texte trop long est coupe plutot que de s'etaler sur
// la moitie du dessin.
.tool-hint-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

// L'opacite ne porte que sur le contenu : le cartouche reste lisible sur un
// fond de dessin noir comme blanc, l'aide reste discrete.
.tool-hint-text,
.tool-hint-icon {
  opacity: 0.7;
}

.tool-hint-icon {
  flex-shrink: 0;
}

.tool-hint-enter-active,
.tool-hint-leave-active {
  transition: opacity 0.15s ease;
}

.tool-hint-enter-from,
.tool-hint-leave-to {
  opacity: 0;
}
</style>
